import { useEffect, useMemo, useRef, type VideoHTMLAttributes } from 'react'
import { AdOverlay } from './components/AdOverlay'
import { AmbientBackdrop } from './components/AmbientBackdrop'
import { BigPlayButton } from './components/BigPlayButton'
import { BrandMark } from './components/BrandMark'
import { ControlsBar } from './components/ControlsBar'
import { EndScreen } from './components/EndScreen'
import { ErrorOverlay } from './components/ErrorOverlay'
import { HotkeysOverlay } from './components/HotkeysOverlay'
import { ResumePrompt } from './components/ResumePrompt'
import { useAutoHideControls } from './hooks/useAutoHideControls'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'
import { usePlayer, type PlayerApi } from './hooks/usePlayer'
import type { PlayerConfig } from './types'
import { isInIframe } from './utils/iframe'
import styles from './YouTubePlayer.module.css'

interface Props {
  config: PlayerConfig
  onApi?: (api: PlayerApi) => void
}

export function YouTubePlayer({ config, onApi }: Props) {
  const player = usePlayer(config)
  const { ui } = player
  const inIframe = isInIframe()
  const allowTheater = !inIframe && config.theater !== false

  useEffect(() => {
    onApi?.(player)
  }, [player, onApi])
  const lastTap = useRef<{ t: number; x: number } | null>(null)
  const gesture = useRef<{ x: number; y: number; vol: number; bri: number; side: 'l' | 'r' } | null>(
    null,
  )

  const pinned =
    ui.settingsPanel != null || ui.infoOpen || ui.hotkeysOpen || !!ui.resumeOffer || !ui.playing

  const { bumpControls } = useAutoHideControls(
    ui.controlsVisible,
    player.setControlsVisible,
    ui.playing,
    pinned,
  )

  const kb = useMemo(
    () => ({
      enabled: true,
      togglePlay: () => void player.togglePlay(),
      seekBy: (d: number) => player.seekBy(d),
      volumeBy: (d: number) => player.volumeBy(d),
      toggleMute: () => player.toggleMute(),
      toggleFullscreen: () => void player.toggleFullscreen(),
      toggleCaptions: () => player.toggleCaptions(),
      toggleTheater: () => {
        if (allowTheater) player.toggleTheater()
      },
      toggleHotkeys: () => player.toggleHotkeys(),
    }),
    [player, allowTheater],
  )

  useKeyboardShortcuts(player.containerRef, kb)

  // Mini-player when scrolled out of view (disabled in iframes)
  useEffect(() => {
    if (inIframe || config.miniPlayer === false) return
    const el = player.containerRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        const shouldMini = !entry.isIntersecting && ui.hasStarted && !ui.ended
        player.setMiniPlayer(shouldMini)
      },
      { threshold: 0.15 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [config.miniPlayer, inIframe, player, ui.hasStarted, ui.ended])

  const showBigPlay = !ui.hasStarted || (ui.ended && !ui.isAdPlaying && !config.endScreen)

  const cueSize =
    ui.captionStyle.fontSize === 'small' ? '0.9em' : ui.captionStyle.fontSize === 'large' ? '1.35em' : '1.1em'

  const onPointerDown = (e: React.PointerEvent) => {
    if (ui.isAdPlaying) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX - rect.left
    const side = x < rect.width / 2 ? 'l' : 'r'
    gesture.current = {
      x: e.clientX,
      y: e.clientY,
      vol: ui.volume,
      bri: ui.brightness,
      side,
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current
    if (!g || e.buttons === 0) return
    const dy = g.y - e.clientY
    if (Math.abs(dy) < 8) return
    if (g.side === 'r') {
      player.setVolume(g.vol + dy / 200)
    } else {
      player.setBrightness(g.bri + dy / 300)
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const g = gesture.current
    gesture.current = null
    if (!g) return
    const moved = Math.hypot(e.clientX - g.x, e.clientY - g.y)
    if (moved > 18) return

    // double-tap seek
    const now = Date.now()
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX - rect.left
    if (lastTap.current && now - lastTap.current.t < 280) {
      if (x < rect.width / 2) player.seekBy(-10)
      else player.seekBy(10)
      lastTap.current = null
      return
    }
    lastTap.current = { t: now, x }
  }

  return (
    <div
      ref={player.containerRef}
      className={[
        styles.player,
        ui.controlsVisible || !ui.playing ? styles.showCursor : styles.hideCursor,
        ui.theater && allowTheater ? styles.theater : '',
        ui.miniPlayer && !inIframe ? styles.mini : '',
      ]
        .filter(Boolean)
        .join(' ')}
      tabIndex={0}
      onMouseMove={bumpControls}
      onTouchStart={bumpControls}
      onFocus={bumpControls}
      style={
        {
          '--pv-cue-size': cueSize,
          '--pv-cue-color': ui.captionStyle.color,
          '--pv-cue-bg': `rgba(0,0,0,${ui.captionStyle.backgroundOpacity})`,
        } as React.CSSProperties
      }
    >
      <AmbientBackdrop
        enabled={!!config.ambient && !ui.fullscreen}
        videoRef={player.videoRef}
        playing={ui.playing && !ui.isAdPlaying}
      />

      <BrandMark dimmed={ui.playing && !ui.controlsVisible} />

      <video
        ref={player.videoRef}
        className={styles.video}
        playsInline
        preload="metadata"
        {...({ 'x-webkit-airplay': 'allow' } as VideoHTMLAttributes<HTMLVideoElement>)}
        disableRemotePlayback={config.cast === false}
        style={{ filter: `brightness(${ui.brightness})` }}
        onClick={() => void player.togglePlay()}
        onDoubleClick={() => void player.toggleFullscreen()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {ui.captionTracks.map((t) => (
          <track
            key={t.src}
            kind="subtitles"
            src={t.src}
            srcLang={t.srclang}
            label={t.label}
            default={t.default}
          />
        ))}
      </video>

      {ui.seekFlash && Date.now() - ui.seekFlash.at < 800 && (
        <div
          className={`${styles.seekFlash} ${ui.seekFlash.dir === 'left' ? styles.left : styles.right}`}
        >
          {ui.seekFlash.dir === 'left' ? '−10s' : '+10s'}
        </div>
      )}

      {ui.buffering && ui.hasStarted && !ui.error && (
        <div className={styles.spinner} aria-hidden>
          <div className={styles.spinnerRing} />
        </div>
      )}

      <BigPlayButton visible={showBigPlay && !ui.error} onClick={() => void player.requestPlay()} />

      <AdOverlay
        visible={ui.isAdPlaying}
        title={ui.adTitle}
        currentTime={ui.adCurrentTime}
        duration={ui.adDuration}
        skipRemaining={ui.adSkipRemaining}
        canSkip={ui.adCanSkip}
        onSkip={() => {
          if (ui.adCanSkip) void player.skipAd()
        }}
        onClickThrough={player.openAdClickThrough}
      />

      <EndScreen
        visible={ui.ended && !ui.isAdPlaying}
        config={config.endScreen}
        onReplay={() => void player.replay()}
      />

      <ErrorOverlay message={ui.error} onRetry={() => void player.retry()} />
      <ResumePrompt
        time={ui.resumeOffer}
        onResume={player.acceptResume}
        onDismiss={player.dismissResume}
      />
      <HotkeysOverlay open={ui.hotkeysOpen} onClose={player.toggleHotkeys} />

      <ControlsBar
        ui={ui}
        visible={ui.controlsVisible || !ui.playing || ui.isAdPlaying}
        thumbnails={config.thumbnails}
        videoRef={player.videoRef}
        enableShare={config.share !== false}
        enableSleep={config.sleepTimer !== false}
        enableCast={config.cast !== false}
        onTogglePlay={() => void player.togglePlay()}
        onSeek={player.seek}
        onVolume={player.setVolume}
        onToggleMute={player.toggleMute}
        onSpeed={player.setPlaybackRate}
        onQuality={(id) => void player.setQuality(id)}
        onAudio={(id) => void player.setAudioTrack(id)}
        onPanel={player.setSettingsPanel}
        onToggleInfo={player.toggleInfo}
        onCaptionStyle={player.setCaptionStyle}
        onSleep={player.setSleepTimer}
        onShare={player.notifyShare}
        onCast={player.setCastingUi}
        onFullscreen={() => void player.toggleFullscreen()}
        onPip={() => void player.togglePip()}
        onTheater={player.toggleTheater}
        allowTheater={allowTheater}
        onCaptions={player.toggleCaptions}
        onJumpLive={player.jumpToLive}
      />
    </div>
  )
}
